import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  HStack,
  IconButton,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion, AnimatePresence } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import type { Product } from "../types";
import shutterGif from "../assets/logo.gif";
import InquiryModal from "./InquiryModal";

const MotionBox = motion(Box);

export const ShutterMark = () => (
  <Image src={shutterGif} alt="Red Shutter" objectFit="contain" />
);

const ProductCard = ({ product }: { product: Product }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const [imgHovered, setImgHovered] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isInquiryOpen,
    onOpen: onInquiryOpen,
    onClose: onInquiryClose,
  } = useDisclosure();

  const images = product.images || [];

  const next = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setImgIndex((p) => (p === images.length - 1 ? 0 : p + 1));
  };
  const prev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setImgIndex((p) => (p === 0 ? images.length - 1 : p - 1));
  };

  return (
    <MotionBox
      as="article"
      bg="rs.surface"
      borderRadius="10px"
      overflow="hidden"
      border="1px solid"
      borderColor="rs.border"
      display="flex"
      flexDirection="column"
      cursor="pointer"
      transition="background 0.18s"
      _hover={{ bg: "rs.surfaceHover", borderColor: "rs.borderStrong" }}
      whileHover={{ y: -3 }}
    >
      {/* Image area */}
      <Box
        as={RouterLink}
        to={`/product/${product.id}`}
        position="relative"
        bg="rs.imageWell"
        h="190px"
        flexShrink={0}
        display="block"
        onMouseEnter={() => setImgHovered(true)}
        onMouseLeave={() => setImgHovered(false)}
        onClick={(e: React.MouseEvent) => {
          if (images.length > 0) {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        {images.length > 0 ? (
          <>
            <Image
              src={images[imgIndex].url}
              alt={product.name}
              h="100%"
              w="100%"
              objectFit="contain"
              p={3}
              loading="lazy"
              transition="opacity 0.2s"
            />
            {images.length > 1 && (
              <AnimatePresence>
                {imgHovered && (
                  <MotionBox
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    position="absolute"
                    inset={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                  >
                    <IconButton
                      aria-label="Previous"
                      icon={<ChevronLeftIcon />}
                      size="sm"
                      bg="blackAlpha.700"
                      color="white"
                      _hover={{ bg: "blackAlpha.900" }}
                      borderRadius="full"
                      onClick={prev}
                    />
                    <IconButton
                      aria-label="Next"
                      icon={<ChevronRightIcon />}
                      size="sm"
                      bg="blackAlpha.700"
                      color="white"
                      _hover={{ bg: "blackAlpha.900" }}
                      borderRadius="full"
                      onClick={next}
                    />
                  </MotionBox>
                )}
              </AnimatePresence>
            )}
            {images.length > 1 && (
              <HStack
                spacing={1}
                position="absolute"
                bottom={2}
                left="50%"
                transform="translateX(-50%)"
              >
                {images.map((_, i) => (
                  <Box
                    key={i}
                    w="5px"
                    h="5px"
                    borderRadius="full"
                    bg={i === imgIndex ? "white" : "whiteAlpha.400"}
                    cursor="pointer"
                    transition="background 0.2s"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImgIndex(i);
                    }}
                  />
                ))}
              </HStack>
            )}
          </>
        ) : (
          <Flex h="100%" align="center" justify="center">
            <ShutterMark />
          </Flex>
        )}
      </Box>

      {/* Card body */}
      <Flex direction="column" flex={1} p={5}>
        <Text
          fontSize="10px"
          fontWeight="600"
          letterSpacing="0.12em"
          textTransform="uppercase"
          color="rs.textDim"
          mb="5px"
        >
          {product.category}
        </Text>

        <Heading
          as={RouterLink}
          to={`/product/${product.id}`}
          fontFamily="'DM Serif Display', serif"
          fontSize="16px"
          fontWeight="400"
          color="rs.text"
          lineHeight="1.3"
          mb={2}
          noOfLines={2}
          _hover={{ color: "rs.red" }}
        >
          {product.name}
        </Heading>

        <Popover
          trigger="hover"
          openDelay={250}
          closeDelay={200}
          placement="top-start"
        >
          <PopoverTrigger>
            <Text
              fontSize="12px"
              color="rs.textMuted"
              noOfLines={2}
              lineHeight="1.65"
              mb={4}
              flex={1}
              cursor="default"
            >
              {product.description}
            </Text>
          </PopoverTrigger>
          <PopoverContent
            maxW="300px"
            bg="rs.surfaceRaised"
            borderColor="rs.borderStrong"
            _focus={{ outline: "none" }}
          >
            <PopoverArrow bg="rs.surfaceRaised" />
            <Box
              p={3}
              maxH="220px"
              overflowY="auto"
              fontSize="12px"
              color="rs.textMuted"
              whiteSpace="pre-wrap"
            >
              {product.description}
            </Box>
          </PopoverContent>
        </Popover>

        <Flex align="center" justify="space-between" mt="auto">
          <Box>
            <Text fontSize="15px" fontWeight="500" color="rs.text">
              {product.price.toLocaleString("en-LK", {
                maximumFractionDigits: 2,
              })}
            </Text>
            <Text fontSize="10px" color="rs.textDim" letterSpacing="0.06em">
              LKR
            </Text>
          </Box>
          <Box
            w="32px"
            h="32px"
            borderRadius="6px"
            bg="rs.redAlpha"
            border="1px solid"
            borderColor="rs.redBorder"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="rs.red"
            fontSize="20px"
            lineHeight={1}
            cursor="pointer"
            transition="background 0.15s, border-color 0.15s"
            _hover={{ bg: "rs.red", borderColor: "rs.red", color: "white" }}
            onClick={onInquiryOpen}
          >
            +
          </Box>
        </Flex>
      </Flex>

      {/* Lightbox */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
          <ModalCloseButton
            color="white"
            bg="blackAlpha.700"
            _hover={{ bg: "blackAlpha.900" }}
            size="lg"
            zIndex={2}
          />
          <ModalBody
            p={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box position="relative" w="100%">
              {images.length > 0 && (
                <Image
                  src={images[imgIndex].url}
                  alt={product.name}
                  maxH="90vh"
                  maxW="100%"
                  objectFit="contain"
                  margin="auto"
                />
              )}
              {images.length > 1 && (
                <>
                  <IconButton
                    aria-label="Previous"
                    icon={<ChevronLeftIcon />}
                    position="absolute"
                    left={4}
                    top="50%"
                    transform="translateY(-50%)"
                    onClick={prev}
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: "blackAlpha.900" }}
                    size="lg"
                    zIndex={2}
                  />
                  <IconButton
                    aria-label="Next"
                    icon={<ChevronRightIcon />}
                    position="absolute"
                    right={4}
                    top="50%"
                    transform="translateY(-50%)"
                    onClick={next}
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: "blackAlpha.900" }}
                    size="lg"
                    zIndex={2}
                  />
                  <HStack
                    spacing={2}
                    position="absolute"
                    bottom={4}
                    left="50%"
                    transform="translateX(-50%)"
                    bg="blackAlpha.600"
                    px={3}
                    py={2}
                    borderRadius="full"
                  >
                    {images.map((_, i) => (
                      <Box
                        key={i}
                        w={3}
                        h={3}
                        borderRadius="full"
                        bg={i === imgIndex ? "white" : "whiteAlpha.500"}
                        cursor="pointer"
                        onClick={() => setImgIndex(i)}
                        transition="all 0.2s"
                        _hover={{ bg: "white" }}
                      />
                    ))}
                  </HStack>
                </>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <InquiryModal
        product={product}
        isOpen={isInquiryOpen}
        onClose={onInquiryClose}
      />
    </MotionBox>
  );
};

export default ProductCard;
